<template>
  <div class="gceo-dashboard">
    <!-- HEADER -->
    <div class="gceo-header">
      <div class="hdr-tab-group">
        <button
          class="hdr-tab"
          :class="{ active: activeTab === 0 }"
          @click="activeTab = 0"
        >
          <v-icon size="13">mdi-view-dashboard-outline</v-icon>
          Momentum Dashboard
        </button>
        <button
          class="hdr-tab"
          :class="{ active: activeTab === 1 }"
          @click="activeTab = 1"
        >
          <v-icon size="13">mdi-map-marker-radius-outline</v-icon>
          Live Maps
        </button>
      </div>
      <div class="header-center">
        <div class="date-range">
          <span class="mdi mdi-calendar-range dr-icon"></span>
          <DateField
            class="dr-field"
            :inputProps="{
              dense: true,
              label: 'Start Date',
              clearable: true,
              'hide-details': 'auto',
              outlined: true,
            }"
            v-model="startDate"
          />
          <span class="dr-sep">→</span>
          <DateField
            class="dr-field"
            :inputProps="{
              dense: true,
              label: 'End Date',
              clearable: true,
              'hide-details': 'auto',
              outlined: true,
            }"
            v-model="endDate"
          />
          <v-btn
            small
            depressed
            color="primary"
            class="apply-btn"
            @click="fetchAll"
          >
            Apply
          </v-btn>
        </div>
      </div>
    </div>

    <!-- BODY -->
    <div v-if="activeTab === 0" class="gceo-body">
      <!-- ROW 1 · KPI -->
      <div class="kpi-row">
        <div
          v-for="(c, i) in kpiCards"
          :key="i"
          class="kpi-card"
          :class="c.cls"
        >
          <span v-if="c.icon === 'dirham'" class="kpi-ico kpi-ico-text"
            >د.إ</span
          >
          <span v-else :class="'mdi kpi-ico ' + c.icon"></span>
          <div class="kpi-txt">
            <div class="kpi-lbl">
              {{ c.label }}<span v-if="c.sub" class="kpi-sub">{{ c.sub }}</span>
            </div>
            <div class="kpi-val">{{ c.value }}</div>
          </div>
        </div>
      </div>

      <!-- ROW 2 · phone frames | bar chart + right panels -->
      <div class="row2">
        <!-- 5 phone frames -->
        <div class="phones-area">
          <div
            class="phone-frame"
            v-for="(group, gi) in phoneGroups"
            :key="gi"
            :style="{ borderTopColor: group[0] && group[0].color }"
          >
            <div class="term-cards">
              <div v-for="(term, ti) in group" :key="ti" class="tc">
                <!-- Colored terminal header -->
                <div
                  class="tc-hdr"
                  :style="{
                    background: term.color + '18',
                    borderBottom: '2px solid ' + term.color,
                  }"
                >
                  <span class="tc-name" :style="{ color: term.color }">{{
                    term.name
                  }}</span>
                </div>

                <!-- Stats row -->
                <div class="tc-top-stats">
                  <div class="tc-stat">
                    <div class="tc-stat-val" :style="{ color: term.color }">
                      {{ formatNum(term.totalLegs) }}
                    </div>
                    <div class="tc-stat-lbl">JOBS</div>
                  </div>
                  <div class="tc-stat">
                    <div class="tc-stat-val" :style="{ color: term.color }">
                      {{ formatNum(term.totalUnits) }}
                    </div>
                    <div class="tc-stat-lbl">UNITS</div>
                  </div>
                  <div class="tc-stat">
                    <div class="tc-stat-val" :style="{ color: term.color }">
                      {{ formatNum(term.totalTeus) }}
                    </div>
                    <div class="tc-stat-lbl">TEU</div>
                  </div>
                </div>

                <!-- Job Type Breakup -->
                <div class="tc-sec-hdr">
                  <span
                    class="tc-sec-dot"
                    :style="{ background: term.color }"
                  ></span>
                  JOB TYPE BREAKUP
                </div>
                <div class="flow-grid">
                  <div
                    v-for="(f, fi) in term.teuFlow"
                    :key="fi"
                    class="flow-item"
                  >
                    <span class="fi-val">{{ formatNum(f.value) }}</span>
                    <span class="fi-lbl">{{ f.label }}</span>
                  </div>
                </div>

                <!-- Lane Breakup -->
                <div class="tc-sec-hdr">
                  <span
                    class="tc-sec-dot"
                    :style="{ background: term.color }"
                  ></span>
                  LANE BREAKUP
                </div>
                <div class="dest-list">
                  <div v-if="!term.destinations.length" class="dest-empty">
                    <span
                      class="mdi mdi-map-marker-off-outline dest-empty-icon"
                    ></span>
                    <span>No data available</span>
                  </div>
                  <div
                    v-for="(d, di) in term.destinations"
                    :key="di"
                    class="dest-row"
                    :style="{ borderLeftColor: d.color }"
                  >
                    <span class="dest-lbl">{{ term.name }}→{{ d.label }}</span>
                    <div class="dest-metrics">
                      <span class="dest-metric">
                        <span class="dm-val">{{ formatNum(d.legs) }}</span>
                        <span class="dm-lbl dm-lbl-legs">Legs</span>
                      </span>
                      <span class="dest-metric">
                        <span class="dm-val">{{ formatNum(d.teu) }}</span>
                        <span class="dm-lbl dm-lbl-teu">TEU</span>
                      </span>
                      <span class="dest-metric">
                        <span class="dm-val">{{ formatNum(d.units) }}</span>
                        <span class="dm-lbl dm-lbl-units">Units</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts area -->
        <div class="charts-area">
          <div class="bar-panel">
            <div class="bar-hdr">
              <span class="mdi mdi-chart-bar"></span>
              Internal vs Sub-Hire <span style="font-size: 9px; color: #94a3b8; font-weight: 600">(TEU)</span>
              <span class="leg">
                <span class="leg-dot" style="background: #14b8a6"></span>
                Total TEU Internal
                <span class="leg-teu" style="color: #14b8a6">{{
                  formatNum(barInternal.reduce((a, b) => a + b, 0))
                }}</span>
              </span>
              <span class="leg">
                <span class="leg-dot" style="background: #f59e0b"></span>
                Total TEU Sub-hire
                <span class="leg-teu" style="color: #f59e0b">{{
                  formatNum(barSubhire.reduce((a, b) => a + b, 0))
                }}</span>
              </span>
            </div>
            <div class="chart-wrap" ref="barWrap">
              <canvas ref="barChart"></canvas>
            </div>
          </div>

          <!-- Container Type Mix (moved here from row3) -->
          <div class="charts-right">
            <div class="panel">
              <div class="p-hdr">CONTAINER TYPE MIX</div>
              <div class="pie-wrap">
                <div class="pie-box" ref="pieWrap">
                  <canvas ref="pieChart"></canvas>
                </div>
                <div class="pie-leg">
                  <div v-if="!containerTypes.length" class="pie-empty">
                    <span class="mdi mdi-chart-pie pe-empty-icon"></span>
                    <span class="pe-empty-txt">No data available</span>
                  </div>
                  <div v-for="(c, i) in containerTypes" :key="i" class="dl-row">
                    <span
                      class="dl-dot"
                      :style="{ background: c.color }"
                    ></span>
                    <span class="dl-lbl">{{ c.label }}</span>
                    <span class="dl-pct">{{ formatNum(c.units) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ROW 3 · Location table -->
      <div class="row3">
        <div class="loc-panel">
          <div class="p-hdr" style="color: #94a3b8">
            LOCATION DETAIL — CONTAINERS, TEUS &amp; FLEET TYPE
          </div>
          <table class="loc-tbl">
            <thead>
              <tr>
                <th>Location</th>
                <th>Total Containers</th>
                <th>Total Legs</th>
                <th>Total TEU</th>
                <th>Total Sub-hire</th>
                <th>Total Internal</th>
                <th>Dominant Type</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, ri) in locationData" :key="ri">
                <td class="loc-name">{{ r.location }}</td>
                <td>{{ formatNum(r.containers) }}</td>
                <td>{{ formatNum(r.totalLegs) }}</td>
                <td>{{ formatNum(r.teus) }}</td>
                <td>
                  <span class="badge sub">{{ formatNum(r.subhire) }}</span>
                </td>
                <td>
                  <span class="badge int">{{ formatNum(r.internal) }}</span>
                </td>
                <td class="dom">{{ r.dominantType }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Right column: Cost vs Revenue + Top Subhire stacked -->
        <div class="row3-right">
          <!-- Cost vs Revenue (moved here from row2) -->
          <div class="panel">
            <div class="p-hdr">
              <span class="mdi mdi-swap-horizontal"></span> COST VS REVENUE
              <span style="font-size: 9px; color: #94a3b8; font-weight: 600"
                >(Sub-hire)</span
              >
              <span style="font-size: 10px; color: #64748b; margin-left: 6px"
                >Top {{ costRevenueLocations.length }} Transporters</span
              >
              <span class="leg"
                ><span class="leg-dot" style="background: #14b8a6"></span
                >Revenue</span
              >
              <span class="leg"
                ><span class="leg-dot" style="background: #f59e0b"></span
                >Cost</span
              >
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    icon
                    x-small
                    v-on="on"
                    @click="showCrDialog = true"
                    style="margin-left: auto"
                  >
                    <v-icon size="15" color="#f59e0b">mdi-eye-outline</v-icon>
                  </v-btn>
                </template>
                <span>Show all transporters</span>
              </v-tooltip>
            </div>
            <div class="cr-chart-wrap">
              <canvas ref="crChart"></canvas>
            </div>
          </div>

          <!-- Transporter list dialog -->
          <v-dialog v-model="showCrDialog" max-width="640" scrollable>
            <v-card style="background: #ffffff; color: #1e293b">
              <v-card-title
                style="
                  font-size: 13px;
                  font-weight: 700;
                  padding: 12px 16px;
                  border-bottom: 1px solid #e2e8f0;
                "
              >
                <v-icon color="#14b8a6" size="16" style="margin-right: 6px"
                  >mdi-swap-horizontal</v-icon
                >
                Cost vs Revenue — All Transporters
                <v-spacer></v-spacer>
                <v-btn icon x-small @click="showCrDialog = false"
                  ><v-icon color="#64748b" size="16">mdi-close</v-icon></v-btn
                >
              </v-card-title>
              <v-card-text
                style="padding: 0; max-height: 460px; overflow-y: auto"
              >
                <table class="cr-dialog-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Transporter</th>
                      <th>Containers</th>
                      <th>Revenue (AED)</th>
                      <th>Cost (AED)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(t, i) in costRevenueLocations" :key="i">
                      <td>{{ i + 1 }}</td>
                      <td class="tr-name">{{ t.transporter_name }}</td>
                      <td>{{ formatNum(t.container_count) }}</td>
                      <td class="td-rev">{{ formatNum(t.revenue) }}</td>
                      <td class="td-cost">{{ formatNum(t.cost) }}</td>
                    </tr>
                  </tbody>
                </table>
              </v-card-text>
            </v-card>
          </v-dialog>

          <div class="panel exit-panel">
            <div class="p-hdr pe-hdr-row">
              <span
                style="
                  display: flex;
                  align-items: center;
                  gap: 4px;
                  min-width: 0;
                "
              >
                <span class="mdi mdi-truck-outline"></span>
                Top Subhire Transport Company
                <span class="p-sub">by TEU</span>
              </span>
              <span class="pe-total">{{ formatNum(portExitTotal) }} TEU</span>
            </div>
            <div class="pe-list" ref="peList" @scroll="onPortExitScroll">
              <div
                v-if="!portExitData.length && !portExitLoading"
                class="pe-empty"
              >
                <span class="mdi mdi-chart-bar-stacked pe-empty-icon"></span>
                <span class="pe-empty-txt">No data available</span>
              </div>
              <div
                v-for="(p, pi) in visiblePortExitData"
                :key="pi"
                class="pe-row"
              >
                <span class="pe-lbl">{{ p.label }}</span>
                <div class="btrack">
                  <div
                    class="bfill"
                    :style="{ width: p.pct + '%', background: p.color }"
                  ></div>
                </div>
                <span class="pe-val" :style="{ color: p.color }">{{
                  formatNum(p.value)
                }}</span>
              </div>
              <div v-if="portExitLoading" class="pe-loading">
                <span class="mdi mdi-loading mdi-spin"></span> Loading...
              </div>
              <div
                v-if="!portExitHasMore && portExitData.length"
                class="pe-end"
              >
                All {{ portExitData.length }} destinations shown
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Live Maps tab -->
    <div v-show="activeTab === 1" class="lm-root">
      <!-- Summary KPI strip -->
      <div class="lm-kpis">
        <div class="lm-kpi">
          <div class="lm-kpi-ico-wrap" style="background:#f1f5f9">
            <span class="mdi mdi-truck-outline" style="color:#334155"></span>
          </div>
          <div class="lm-kpi-txt">
            <div class="lm-kpi-val">{{ lmUniqueVehicleCount }}</div>
            <div class="lm-kpi-lbl">Total Vehicles</div>
          </div>
        </div>
        <div class="lm-kpi">
          <div class="lm-kpi-ico-wrap" style="background:#f0fdf4">
            <span class="mdi mdi-map-marker-path" style="color:#334155"></span>
          </div>
          <div class="lm-kpi-txt">
            <div class="lm-kpi-val">{{ lmTrips.length }}</div>
            <div class="lm-kpi-lbl">Total Trips</div>
          </div>
        </div>
        <div class="lm-kpi">
          <div class="lm-kpi-ico-wrap" style="background:#ccfbf1">
            <span class="mdi mdi-truck-fast" style="color:#14b8a6"></span>
          </div>
          <div class="lm-kpi-txt">
            <div class="lm-kpi-val" style="color:#14b8a6">{{ lmActiveCount }}</div>
            <div class="lm-kpi-lbl">Active Trips</div>
          </div>
        </div>
        <div class="lm-kpi">
          <div class="lm-kpi-ico-wrap" style="background:#fefce8">
            <span class="mdi mdi-truck-outline" style="color:#ca8a04"></span>
          </div>
          <div class="lm-kpi-txt">
            <div class="lm-kpi-val" style="color:#ca8a04">{{ lmIdleCount }}</div>
            <div class="lm-kpi-lbl">Idle Vehicles</div>
          </div>
        </div>
        <div class="lm-kpi">
          <div class="lm-kpi-ico-wrap" style="background:#fff1f2">
            <span class="mdi mdi-wrench-outline" style="color:#ef4444"></span>
          </div>
          <div class="lm-kpi-txt">
            <div class="lm-kpi-val" style="color:#ef4444">{{ lmMaintenanceCount }}</div>
            <div class="lm-kpi-lbl">In Maintenance Vehicles</div>
          </div>
        </div>
        <v-btn icon small class="lm-refresh-btn" @click="fetchLiveMaps" title="Refresh">
          <v-icon size="18" color="#64748b">mdi-refresh</v-icon>
        </v-btn>
      </div>

      <!-- Body: trip list + map -->
      <div class="lm-body">
        <!-- Trip list panel -->
        <div class="lm-list-panel">
          <div class="lm-list-hdr">
            <span class="mdi mdi-format-list-bulleted"></span> TRIP LIST
            <span class="lm-list-count">{{ lmTrips.length }}</span>
          </div>
          <v-data-table
            :headers="lmHeaders"
            :items="lmFilteredTrips"
            :items-per-page="20"
            :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
            dense
            class="lm-datatable"
            @click:row="(_, idx) => { lmSelectedTrip = idx }"
          >
            <template #top>
              <div class="lm-dt-search">
                <v-text-field
                  v-model="lmSearch"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Search fleet / trip / status..."
                  dense
                  outlined
                  hide-details
                  clearable
                  class="lm-search-field"
                />
              </div>
            </template>
            <template v-slot:[`item.trip_status`]="{ item }">
              <span class="lm-badge" :class="lmStatusClass(item.trip_status)">
                {{ item.trip_status || '—' }}
              </span>
            </template>
            <template v-slot:[`item.trip_id`]="{ item }">
              <span class="lm-trip-id">{{ item.trip_id || '—' }}</span>
            </template>
            <template #no-data>
              <div class="lm-empty">
                <span class="mdi mdi-truck-outline" style="font-size:28px;color:#cbd5e1"></span>
                <span>No trips found</span>
              </div>
            </template>
          </v-data-table>
        </div>

        <!-- Map panel -->
        <div class="lm-map-panel">
          <div id="lm-gmap" style="width:100%;height:calc(100vh - 210px);"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

const PIE_COLORS = [
  "#3b82f6",
  "#1e40af",
  "#7dd3fc",
  "#bbf7d0",
  "#d1d5db",
  "#f97316",
  "#a855f7",
  "#14b8a6",
];

export default {
  name: "GceoDashboard",
  components: { DateField },

  data() {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    return {
      lastUpdate: "",
      startDate: `${y}-${m}-01`,
      endDate: `${y}-${m}-${lastDay}`,

      activeTab: 0,
      showCrDialog: false,

      // Live Maps
      lmTrips: [],
      lmMarkers: [],
      lmMap: null,
      lmClusterer: null,
      lmGoogleMarkers: [],
      lmSearch: "",
      lmSelectedTrip: null,
      lmPage: 1,
      lmPageSize: 10,
      lmHeaders: [
        { text: "Fleet No", value: "fleet_number", sortable: true },
        { text: "Trip ID", value: "trip_id", sortable: true },
        { text: "Status", value: "trip_status", sortable: true },
      ],

      // API-driven state
      summaryData: null,
      dailyExitData: [],
      costRevenueLocations: [],
      transporterData: [],
      locationDeepDive: [],
      containerTypeMixData: [],

      // Scroll pagination for port exit
      portExitData: [],
      portExitPage: 1,
      portExitPageSize: 30,
      portExitLoading: false,
      portExitHasMore: true,
      portExitTotal: 0,
    };
  },

  computed: {
    lmFilteredTrips() {
      const q = (this.lmSearch || "").toLowerCase();
      if (!q) return this.lmTrips;
      return this.lmTrips.filter(
        (t) =>
          (t.fleet_number || "").toLowerCase().includes(q) ||
          (t.trip_id || "").toLowerCase().includes(q) ||
          (t.driver_name || "").toLowerCase().includes(q),
      );
    },
    lmVisibleTrips() {
      return this.lmFilteredTrips.slice(0, this.lmPage * this.lmPageSize);
    },
    lmActiveCount() {
      return this.lmTrips.filter((t) =>
        (t.trip_status || "").toLowerCase().includes("on trip") ||
        (t.trip_status || "").toLowerCase().includes("on_trip") ||
        (t.trip_status || "").toLowerCase().includes("active"),
      ).length;
    },
    lmUniqueVehicleCount() {
      const fleet = new Set(this.lmTrips.map((t) => t.fleet_number).filter((f) => f && f !== "—"));
      return fleet.size;
    },
    lmIdleCount() {
      return this.lmTrips.filter((t) =>
        (t.trip_status || "").toLowerCase().includes("idle"),
      ).length;
    },
    lmMaintenanceCount() {
      return this.lmTrips.filter((t) =>
        (t.trip_status || "").toLowerCase().includes("maintenance"),
      ).length;
    },
    dateParams() {
      const p = {};
      if (this.startDate) p.start_date = this.startDate;
      if (this.endDate) p.end_date = this.endDate;
      return p;
    },

    kpiCards() {
      const s = this.summaryData;
      return [
        {
          label: "Total Container",
          icon: "mdi-package-variant",
          cls: "kpi-blue",
          value: s ? this.formatNum(s.total_containers) : "—",
        },
        {
          label: "Total Legs",
          icon: "mdi-road-variant",
          cls: "kpi-green",
          value: s ? this.formatNum(s.total_legs) : "—",
        },
        {
          label: "Total TEU",
          icon: "dirham",
          cls: "kpi-total",
          value: s ? this.formatNum(s.total_teu_count) : "—",
        },
        {
          label: "Total Sub-hire",
          icon: "mdi-truck-outline",
          cls: "kpi-orange",
          value: s ? this.formatNum(s.total_subhire) : "—",
        },
        {
          label: "Total Internal",
          icon: "mdi-truck-check",
          cls: "kpi-amber",
          value: s ? this.formatNum(s.total_internal) : "—",
        },
        {
          label: "Total Revenue",
          sub: "(Internal + Sub-hire)",
          icon: "mdi-trending-up",
          cls: "kpi-revenue",
          value: s ? `AED ${this.formatNum(s.total_revenue)}` : "—",
        },
        {
          label: "Total Cost",
          sub: "(Sub-hire)",
          icon: "mdi-trending-down",
          cls: "kpi-cost",
          value: s ? `AED ${this.formatNum(s.total_cost)}` : "—",
        },
      ];
    },

    allJobTypes() {
      if (this.summaryData && this.summaryData.job_type_breakup) {
        return this.summaryData.job_type_breakup.map((j) => j.job_type);
      }
      return ["Import", "Export", "FCL", "LCL", "Empty"];
    },

    // One phone frame per tracked location
    phoneGroups() {
      const termColors = [
        "#14b8a6",
        "#3b82f6",
        "#f59e0b",
        "#8b5cf6",
        "#f97316",
      ];
      return this.locationDeepDive.map((loc, idx) => [
        {
          name: loc.location_code,
          color: termColors[idx % termColors.length],
          totalTeus: this.locTotalTeu(loc),
          totalUnits: loc.total_units || 0,
          totalTeu: this.locTotalTeu(loc),
          totalLegs: loc.total_legs || 0,
          teuFlow: (() => {
            const flowMap = {};
            (loc.volume_by_job_type || []).forEach((j) => {
              flowMap[j.job_type] = j.teu;
            });
            return this.allJobTypes.map((jt) => ({
              value: flowMap[jt] || 0,
              label: jt,
            }));
          })(),
          containerMix: (loc.equipment_mix || []).slice(0, 4).map((e, i) => ({
            value: e.unit_count,
            label: e.label,
            color: PIE_COLORS[i % PIE_COLORS.length],
          })),
          destinations: (() => {
            const dest = loc.destination_mapping || [];
            const maxTeu = dest.length
              ? Math.max(...dest.map((d) => d.teu || 0))
              : 1;
            return dest.map((d, i) => ({
              label: d.destination_code || d.destination_name || "—",
              teu: d.teu || 0,
              legs: d.legs || 0,
              units: d.units || 0,
              pct: maxTeu > 0 ? Math.round(((d.teu || 0) / maxTeu) * 100) : 0,
              color: PIE_COLORS[i % PIE_COLORS.length],
            }));
          })(),
        },
      ]);
    },

    // Bar chart labels & datasets derived from daily exit fleet API
    barLabels() {
      return this.dailyExitData.map((r) => (r.date ? r.date.slice(5) : ""));
    },
    barInternal() {
      return this.dailyExitData.map((r) =>
        r.internal_metrics ? r.internal_metrics.total_teu : 0,
      );
    },
    barSubhire() {
      return this.dailyExitData.map((r) =>
        r.subhire_metrics ? r.subhire_metrics.total_teu : 0,
      );
    },

    // Container Type Mix for pie chart
    containerTypes() {
      return this.containerTypeMixData.map((c, i) => ({
        label: c.type_name,
        color: PIE_COLORS[i % PIE_COLORS.length],
        pct: c.unit_percentage || 0,
        units: c.unit_count || 0,
      }));
    },

    // Location table rows
    locationData() {
      return this.locationDeepDive.map((loc) => {
        const sub = loc.subhire_units || 0;
        const internal = loc.internal_units || 0;
        const dominant = (loc.equipment_mix || [])[0];
        return {
          location: loc.location_code,
          containers: loc.total_units || 0,
          totalLegs: loc.total_legs || 0,
          teus: Math.round(this.locTotalTeu(loc)),
          subhire: sub,
          internal: internal,
          dominantType: dominant
            ? `${dominant.label} (${dominant.unit_count})`
            : "—",
        };
      });
    },

    visiblePortExitData() {
      return this.portExitData.slice(
        0,
        this.portExitPage * this.portExitPageSize,
      );
    },
  },

  watch: {
    activeTab(val) {
      if (val === 0) {
        this.$nextTick(() => {
          this.renderBar();
          this.renderPie();
          this.renderCrChart();
        });
      } else if (val === 1) {
        this.fetchLiveMaps();
      }
    },
    lmSearch() {
      this.lmPage = 1;
    },
    containerTypes() {
      this.$nextTick(() => this.renderPie());
    },
    barLabels() {
      this.$nextTick(() => this.renderBar());
    },
    costRevenueLocations() {
      this.$nextTick(() => this.renderCrChart());
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.renderBar();
      this.renderPie();
      this.renderCrChart();
      this.fetchAll();
    });
  },

  beforeDestroy() {
    if (this._bar) this._bar.destroy();
    if (this._pie) this._pie.destroy();
    if (this._cr) this._cr.destroy();
  },

  methods: {
    fetchLiveMaps() {
      this.$bus.$emit("showLoader", true);
      this.$api.dashboard
        .getManagementLiveTripData({ limit: "all" })
        .then((res) => {
          const data = res.data || [];
          this.lmPage = 1;
          this.lmMarkers = data.filter((t) => t.map_data).map((t) => t.map_data);
          this.lmTrips = data.map((t) => ({
            fleet_number: t.map_data?.details?.fleet_number || "—",
            trip_id: t.map_data?.details?.trip_id || "—",
            trip_status: t.map_data?.details?.vehicle_status || "—",
            driver_name: t.map_data?.details?.driver || "—",
          }));
          this.$nextTick(() => this.initLiveMap());
        })
        .catch(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Could not load live map data",
            color: "error",
          });
        })
        .finally(() => this.$bus.$emit("showLoader", false));
    },

    initLiveMap() {
      const el = document.getElementById("lm-gmap");
      if (!el || !window.google) return;

      if (!this.lmMap) {
        this.lmMap = new window.google.maps.Map(el, {
          zoom: 8,
          center: { lat: 25.352793510570958, lng: 56.367735996304596 }, // KCT – Khorfakkan Container Terminal
          disableDefaultUI: true,
          streetViewControl: false,
          mapTypeControl: false,
        });
      }

      this.renderLiveMapMarkers();
    },

    renderLiveMapMarkers() {
      if (!this.lmMap) return;

      // Clear old markers from clusterer and map
      if (this.lmClusterer) {
        this.lmClusterer.clearMarkers();
      }
      this.lmGoogleMarkers.forEach((m) => m.setMap(null));
      this.lmGoogleMarkers = [];

      if (!this.lmMarkers.length) return;

      const bounds = new window.google.maps.LatLngBounds();

      this.lmMarkers.forEach((markerData) => {
        const pos = markerData.position;
        if (!pos) return;

        const gMarker = new window.google.maps.Marker({
          position: { lat: pos.lat, lng: pos.lng },
          icon: this.lmCreateTruckIcon(markerData.color || "#64748b"),
        });

        // Info window
        const details = markerData.details || {};
        const iwId = "lmiw_" + Math.random().toString(36).slice(2, 10);
        const row = (icon, label, value, color) =>
          value ? `<div style="display:flex;align-items:center;gap:8px;padding:5px 0;border-bottom:1px solid #f1f5f9">
            <span style="font-size:14px">${icon}</span>
            <span style="flex:1;font-size:11px;color:#64748b">${label}</span>
            <b style="font-size:11px;color:${color || "#334155"}">${value}</b>
          </div>` : "";
        const content = `
          <div style="font-family:sans-serif;width:340px;border-radius:10px;overflow:hidden;box-shadow:0 4px 16px rgba(0,0,0,0.15)">
            <div style="background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:10px 14px;font-size:13px;font-weight:700;display:flex;align-items:center;justify-content:space-between">
              <span>🚚 ${details.fleet_number || "Vehicle"}</span>
              <button onclick="window['${iwId}']&&window['${iwId}']()" style="background:rgba(255,255,255,0.2);border:none;color:#fff;border-radius:50%;width:22px;height:22px;cursor:pointer;font-size:14px;line-height:1;display:flex;align-items:center;justify-content:center;padding:0">&times;</button>
            </div>
            <div style="background:#fff;padding:6px 14px 10px">
              ${row("📍", "Trip ID", details.trip_id, "#2563eb")}
              ${row("📊", "Vehicle Status", details.vehicle_status, details.status_color || "#64748b")}
              ${row("🚚", "Vehicle Type", details.vehicle_type, "#008000")}
              ${row("👤", "Driver", details.driver, "#334155")}
              ${row("⏱️", "Status Duration", details.status_duration, "#008000")}
              ${row("🔢", "GPS ID", details.gps_id, "#FF9800")}
              ${row("🔢", "Odometer", details.odometer_reading ? details.odometer_reading + " kms" : null, "#FF9800")}
              ${row("⚡", "Speed", details.speed ? details.speed + " km/h" : null, "#334155")}
              ${details.container_nos && details.container_nos.length ? row("📦", "Container", details.container_nos.join(", "), "#334155") : ""}
            </div>
          </div>`;

        const infoWindow = new window.google.maps.InfoWindow({ content, maxWidth: 360, disableAutoPan: false });
        window[iwId] = () => infoWindow.close();
        gMarker.addListener("click", () => {
          if (this._lmOpenInfoWindow) this._lmOpenInfoWindow.close();
          infoWindow.open(this.lmMap, gMarker);
          this._lmOpenInfoWindow = infoWindow;
        });

        bounds.extend(gMarker.getPosition());
        this.lmGoogleMarkers.push(gMarker);
      });

      this.lmClusterer = new MarkerClusterer({
        map: this.lmMap,
        markers: this.lmGoogleMarkers,
      });

      const kct = { lat: 25.352793510570958, lng: 56.367735996304596 };
      if (this.lmGoogleMarkers.length > 0) {
        this.lmMap.fitBounds(bounds);
      }
      setTimeout(() => {
        this.lmMap.panTo(kct);
        this.lmMap.setZoom(15);
      }, 1200);
    },

    lmCreateTruckIcon(color) {
      const c = color || "#34a853";
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" xml:space="preserve" viewBox="0 0 64 56">
        <path d="m3 28.3 13.1-7.5 47.9 24-14 7z" opacity=".3" fill="#ea4335"/>
        <path fill="#2d342f" d="M5.3 25v3.8l2.5-1.3v-3.7z"/>
        <path d="M5.3 25v3.8l2.5-1.3v-3.7z" opacity=".39" fill="#ea4335"/>
        <path fill="#2d342f" d="M46.2 42.9c-.7-.4-1.4-.4-1.9-.2l-1.4.7c-.6.2-.9.8-.9 1.6 0 1.5 1.3 3.4 2.8 4.2.8.4 1.5.4 2 .2 0 0 1.4-.7 1.5-.8.5-.3.7-.8.7-1.5 0-1.6-1.3-3.5-2.8-4.2z"/>
        <path d="M46.8 49.3c.2-.1 1.4-.7 1.5-.7.5-.3.7-.8.7-1.5 0-.6-.2-1.3-.5-1.9l-1.4.7c.3.6.5 1.3.5 1.9 0 .7-.3 1.3-.8 1.5z" opacity=".39" fill="#ea4335"/>
        <path fill="#999" d="M46.9 47.4c0 1.2-.9 1.6-2.1 1s-2.1-2-2.1-3.2.9-1.6 2.1-1 2.1 2 2.1 3.2z"/>
        <path fill="#2d342f" d="M45.8 46.7c0 .5-.4.7-.9.4-.5-.2-.9-.8-.9-1.3s.4-.7.9-.4c.6.2.9.8.9 1.3z"/>
        <path fill="#2d342f" d="M9.7 24.6c-.7-.4-1.4-.4-1.9-.2l-1.4.7c-.5.2-.9.8-.9 1.6 0 1.5 1.3 3.4 2.8 4.2.8.4 1.5.4 2 .2 0 0 1.4-.7 1.5-.8.5-.3.7-.8.7-1.5 0-1.5-1.3-3.4-2.8-4.2z"/>
        <path d="M10.3 31.1c.2-.1 1.4-.7 1.5-.7.5-.3.7-.8.7-1.5 0-.6-.2-1.3-.5-1.9l-1.4.7c.3.6.5 1.3.5 1.9 0 .7-.3 1.2-.8 1.5z" opacity=".39" fill="#ea4335"/>
        <path fill="#999" d="M10.4 29.2c0 1.2-.9 1.6-2.1 1s-2.1-2-2.1-3.2.9-1.6 2.1-1 2.1 2 2.1 3.2z"/>
        <path fill="#2d342f" d="M9.4 28.5c0 .5-.4.7-.9.4-.5-.2-.9-.8-.9-1.3s.4-.7.9-.4c.5.2.9.8.9 1.3z"/>
        <path fill="${c}" d="M17 1 3 8v15.3l38 19L55 35V20z"/>
        <path fill="#2d342f" d="M3 22v2l38 19v-2zM55 34v2l-14 7v-2z"/>
        <path d="M3 8v16l38 19V27z" opacity=".06" fill="#ea4335"/>
        <path d="M55 20v16l-14 7V27z" opacity=".39" fill="#ea4335"/>
        <path fill="#e5e5e5" d="m62 28-6-3-14 7v11.3c0 .3.4.5.7.3 1.2-1 3.3-.3 4.8 1.5.9 1.1 1.3 2.3 1.3 3.3l1.2.6 14-7v-8l-2-6z"/>
        <path fill="#4285f4" d="m50.4 40.5-1.7-5c-.1-.2 0-.3.2-.4l12.5-6.2c.2-.1.4 0 .5.2l1.6 4.7c.1.2 0 .3-.2.4l-12.4 6.5c-.2.1-.5 0-.5-.2z"/>
        <path fill="#2d342f" d="m63.4 33.8-.1-.3c0 .2 0 .3-.2.4l-12.4 6.5c-.2.1-.4 0-.5-.2l.1.3c.1.2.3.3.5.2l12.4-6.5c.2-.1.3-.2.2-.4z"/>
        <path fill="#fff" d="m53.5 40 7-3.5v5.8l-7 3.5z"/>
        <path fill="#2d342f" d="m54 45 6-3v-4.7l-6 3zM64 42v-2l-14 7v2zM48 48l2 1v-2l-2-1z"/>
        <path fill="#fff" d="M54 45v1l6-3v-1z"/>
        <path fill="#4285f4" d="m47.5 35.3-3.8-1.9c-.1-.1-.3 0-.3.2v3c0 .2.1.4.3.4l1.3.7c.1.1.2.1.3.2l1.3 1.6h.1l2.2 1.1c.1.1.3-.1.2-.2l-1.7-5c.2 0 .2-.1.1-.1z"/>
        <path fill="#fbbc05" d="M50 45v2l-.5-.2v-2z"/>
        <path fill="#e5e5e5" d="M54 41v.3l6-3V38zM54 42v.3l6-3V39zM54 43v.3l6-3V40zM54 44v.3l6-3V41z"/>
        <path fill="#2d342f" d="M57.5 33.9c-.1-.1-.2-.1-.3 0l-3.8 4.3c-.1.1-.1.2 0 .3s.2.1.3 0l1-1.2v1h.3V37l2.5-2.9c.1 0 .1-.2 0-.2zM61.1 31.9c-.1-.1-.2-.1-.3 0L57 36.2c-.1.1-.1.2 0 .3.1.1.2.1.3 0l.9-1v1.1h.3v-1.4l2.6-3c.1-.1.1-.2 0-.3z"/>
        <path fill="#fff" d="m62 41 2-1v-2l-1 .5c-.6.3-1 .9-1 1.6v.9zM50 47l2-1v-.9c0-.5-.5-.8-1-.6l-1 .5v2z"/>
        <path d="m48 35-6-3v11.3c0 .3.4.5.7.3 1.2-1 3.3-.3 4.8 1.5.3.3.5.6.6 1L48 46v2l2 1v-8l-2-6z" opacity=".06" fill="#ea4335"/>
        <path d="m62 28-14 7 2 6v8l14-7v-8z" opacity=".39" fill="#ea4335"/>
      </svg>`;
      return {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
        scaledSize: new window.google.maps.Size(56, 49),
        anchor: new window.google.maps.Point(14, 35),
        labelOrigin: new window.google.maps.Point(14, 54),
      };
    },

    onLmScroll(e) {
      const el = e.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        if (this.lmVisibleTrips.length < this.lmFilteredTrips.length) {
          this.lmPage++;
        }
      }
    },
    lmStatusClass(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("on trip") || s.includes("on_trip")) return "lm-badge-green";
      if (s.includes("idle")) return "lm-badge-yellow";
      if (s.includes("maintenance")) return "lm-badge-red";
      return "lm-badge-grey";
    },

    fetchAll() {
      this.lastUpdate = new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
      const p = this.dateParams;
      this.$bus.$emit("showLoader", true);
      Promise.all([
        this.$api.dashboard.getManagementDashboardSummary(p),
        this.$api.dashboard.getManagementDashboardDailyExitFleet(p),
        this.$api.dashboard.getManagementDashboardTransporterAnalytics(p),
        this.$api.dashboard.getManagementDashboardLocationDeepDive(p),
        this.$api.dashboard.getManagementDashboardContainerTypeMix(p),
        this.$api.dashboard.getManagementDashboardCostRevenue(p),
      ])
        .then(
          ([
            summary,
            fleet,
            transporter,
            location,
            containerMix,
            costRevenue,
          ]) => {
            this.summaryData = summary.data;
            this.dailyExitData = fleet.data.time_series || [];
            this.costRevenueLocations = costRevenue.data.transporters || [];
            this.transporterData = transporter.data.transporters || [];
            this.locationDeepDive = location.data.locations || [];
            this.containerTypeMixData = containerMix.data.container_types || [];
            this.portExitTotal =
              transporter.data.total_teu_all_transporters || 0;
            this.buildPortExitData();
          },
        )
        .catch(() => {})
        .finally(() => this.$bus.$emit("showLoader", false));
    },

    buildPortExitData() {
      const colors = [
        "#3b82f6",
        "#22c55e",
        "#f97316",
        "#fb923c",
        "#a855f7",
        "#ec4899",
        "#14b8a6",
        "#84cc16",
        "#7dd3fc",
        "#475569",
      ];
      const sorted = [...this.transporterData].sort(
        (a, b) => b.total_teu - a.total_teu,
      );
      const total = sorted.reduce((s, t) => s + t.total_teu, 0);
      this.portExitData = sorted.map((t, i) => ({
        label: t.transporter_name,
        value: Math.round(t.total_teu),
        pct: total ? Math.round((t.total_teu / total) * 100) : 0,
        color: colors[i % colors.length],
      }));
      this.portExitPage = 1;
      this.portExitHasMore = this.portExitData.length > this.portExitPageSize;
    },

    onPortExitScroll(e) {
      const el = e.target;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 16;
      if (nearBottom && !this.portExitLoading && this.portExitHasMore) {
        this.portExitLoading = true;
        setTimeout(() => {
          this.portExitPage += 1;
          this.portExitHasMore =
            this.portExitPage * this.portExitPageSize <
            this.portExitData.length;
          this.portExitLoading = false;
        }, 300);
      }
    },

    renderBar() {
      if (this._bar) {
        this._bar.destroy();
        this._bar = null;
      }
      const wrap = this.$refs.barWrap;
      const canvas = this.$refs.barChart;
      if (!wrap || !canvas) return;
      canvas.width = wrap.clientWidth;
      canvas.height = wrap.clientHeight;
      const barLabels = this.barLabels.length
        ? this.barLabels
        : Array.from({ length: 7 }, (_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - (6 - i));
            return `${d.getDate()}/${d.getMonth() + 1}`;
          });
      const barInternal = this.barInternal.length
        ? this.barInternal
        : Array(7).fill(0);
      const barSubhire = this.barSubhire.length
        ? this.barSubhire
        : Array(7).fill(0);

      this._bar = new Chart(canvas.getContext("2d"), {
        type: "bar",
        data: {
          labels: barLabels,
          datasets: [
            {
              label: "Total TEU Internal",
              backgroundColor: "#14b8a6",
              stack: "s",
              data: barInternal,
            },
            {
              label: "Total TEU Sub-hire",
              backgroundColor: "#f59e0b",
              stack: "s",
              data: barSubhire,
            },
          ],
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [
              {
                stacked: true,
                gridLines: { display: false },
                ticks: {
                  fontSize: 8,
                  fontColor: "#94a3b8",
                  fontStyle: "bold",
                  maxRotation: 0,
                },
              },
            ],
            yAxes: [
              {
                stacked: true,
                gridLines: { color: "#e2e8f0" },
                ticks: { fontSize: 8, fontColor: "#94a3b8" },
              },
            ],
          },
          tooltips: { mode: "index", intersect: false },
        },
      });
    },

    renderPie() {
      if (this._pie) {
        this._pie.destroy();
        this._pie = null;
      }
      const wrap = this.$refs.pieWrap;
      const canvas = this.$refs.pieChart;
      if (!wrap || !canvas) return;
      canvas.width = wrap.clientWidth;
      canvas.height = wrap.clientHeight;

      const hasData = this.containerTypes.length > 0;
      const labels = hasData
        ? this.containerTypes.map((c) => c.label)
        : ["No Data"];
      const data = hasData ? this.containerTypes.map((c) => c.pct) : [1];
      const colors = hasData
        ? this.containerTypes.map((c) => c.color)
        : ["#e2e8f0"];

      this._pie = new Chart(canvas.getContext("2d"), {
        type: "pie",
        data: {
          labels,
          datasets: [
            {
              data,
              backgroundColor: colors,
              borderWidth: 2,
              borderColor: "#fff",
            },
          ],
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          legend: { display: false },
        },
      });
    },

    renderCrChart() {
      if (this._cr) {
        this._cr.destroy();
        this._cr = null;
      }
      const canvas = this.$refs.crChart;
      if (!canvas) return;
      const items = this.costRevenueLocations;
      const hasData = items.length > 0;
      const labels = hasData
        ? items.map((r) => r.transporter_name)
        : ["No Data"];
      const revData = hasData ? items.map((r) => r.revenue || 0) : [0];
      const costData = hasData ? items.map((r) => r.cost || 0) : [0];
      this._cr = new Chart(canvas.getContext("2d"), {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Revenue",
              data: revData,
              backgroundColor: "#14b8a6",
              borderRadius: 3,
            },
            {
              label: "Cost",
              data: costData,
              backgroundColor: "#f59e0b",
              borderRadius: 3,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { bottom: 10 } },
          title: {
            display: true,
            text: `Top ${items.length} Transporters`,
            fontColor: "#94a3b8",
            fontSize: 10,
            padding: 4,
          },
          scales: {
            xAxes: [
              {
                ticks: {
                  fontSize: 9,
                  fontColor: "#1e293b",
                  fontStyle: "bold",
                  maxRotation: 45,
                  minRotation: 45,
                  callback: (v) => (v.length > 12 ? v.slice(0, 11) + "…" : v),
                },
                gridLines: { display: false },
              },
            ],
            yAxes: [
              {
                ticks: {
                  fontSize: 7,
                  fontColor: "#94a3b8",
                  callback: (v) => v.toLocaleString(),
                },
                gridLines: { color: "rgba(148,163,184,0.1)" },
              },
            ],
          },
          legend: {
            display: true,
            labels: { fontColor: "#94a3b8", fontSize: 9, boxWidth: 10 },
          },
          tooltips: {
            mode: "index",
            callbacks: {
              title: (items2) => {
                const idx = items2[0] && items2[0].index;
                return hasData
                  ? (this.costRevenueLocations[idx] || {}).transporter_name ||
                      ""
                  : "";
              },
              label: (item, d) => {
                const amt = Number(item.yLabel).toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                });
                return ` ${d.datasets[item.datasetIndex].label}: AED ${amt}`;
              },
            },
          },
        },
      });
    },

    // ── helpers ──────────────────────────────────────────────────────────────
    locTotalTeu(loc) {
      return (loc.volume_by_job_type || []).reduce((s, j) => s + j.teu, 0);
    },
    formatNum(n) {
      if (n === null || n === undefined || n === "—") return "—";
      const num = parseFloat(n);
      if (isNaN(num)) return n;
      return num.toLocaleString("en-US", { maximumFractionDigits: 1 });
    },
  },
};
</script>

<style scoped>
/* ── tokens ──────────────────────────────────── */
.gceo-dashboard {
  --bdr: #e2e8f0;
  --bg: #f1f5f9;
  --sf: #ffffff;
  --txt: #1e293b;
  --mu: #64748b;
  --dk: #0f172a;
  --pr: #e8500a;
  --sh: 0 1px 3px rgba(0, 0, 0, 0.08);
  --r: 8px;
}
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.gceo-dashboard {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  background: var(--bg);
  color: var(--txt);
  font-family: "DM Sans", "Inter", sans-serif;
  font-size: 11px;
}

/* ── header ───────────────────────────────────── */
.gceo-header {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  background: var(--sf);
  border-bottom: 1px solid var(--bdr);
  flex-shrink: 0;
  gap: 8px;
  overflow: hidden;
}
.hdr-tab-group {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  flex-shrink: 0;
}
.hdr-tab {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  outline: none;
}
.hdr-tab:hover {
  color: #1e293b;
  background: #e2e8f0;
}
.hdr-tab.active {
  background: #ffffff;
  color: #f59e0b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
.hdr-tab .v-icon {
  color: inherit !important;
}
.gceo-coming-soon {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.cs-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--txt);
}
.cs-sub {
  font-size: 13px;
  color: var(--mu);
  letter-spacing: 2px;
  text-transform: uppercase;
}
/* ── Live Maps ─────────────────────────────────────────────────────────── */
.lm-root {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: 6px;
  padding: 6px 8px 8px;
}
.lm-kpis {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.lm-kpi {
  background: var(--sf);
  border: 1px solid var(--bdr);
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-height: 68px;
}
.lm-kpi-ico-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 20px;
}
.lm-kpi-txt {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.lm-refresh-btn {
  flex-shrink: 0;
  margin-left: auto;
  align-self: center;
  background: var(--sf) !important;
  border: 1px solid var(--bdr) !important;
  border-radius: 8px !important;
}
.lm-kpi-val {
  font-size: 22px;
  font-weight: 900;
  color: var(--txt);
  line-height: 1;
}
.lm-kpi-lbl {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--mu);
  letter-spacing: 0.4px;
  white-space: nowrap;
}
.lm-body {
  flex: 1;
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 6px;
  min-height: 0;
}
.lm-list-panel {
  background: var(--sf);
  border: 1px solid var(--bdr);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: calc(100vh - 200px);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.lm-list-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #1976d2;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  padding: 0 5px;
  line-height: 1;
}
.lm-list-hdr {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 800;
  color: var(--txt);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--bdr);
  flex-shrink: 0;
  background: #f8fafc;
}
.lm-search {
  margin-left: auto;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  padding: 3px 8px;
  font-size: 11px;
  outline: none;
  width: 140px;
  color: var(--txt);
}
.lm-col-hdr {
  display: flex;
  padding: 4px 10px;
  background: #f8fafc;
  border-bottom: 1px solid var(--bdr);
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  color: var(--mu);
  letter-spacing: 0.4px;
  flex-shrink: 0;
}
.lm-list-body {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}
.lm-list-body::-webkit-scrollbar { width: 4px; }
.lm-list-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
.lm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  font-size: 12px;
  color: #94a3b8;
}
.lm-list-cols {
  display: grid;
  grid-template-columns: 110px 1fr 90px;
  padding: 4px 10px;
  background: #f8fafc;
  border-bottom: 1px solid var(--bdr);
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  color: var(--mu);
  letter-spacing: 0.4px;
  flex-shrink: 0;
}
.lm-list-row {
  display: grid;
  grid-template-columns: 110px 1fr 90px;
  align-items: center;
  padding: 6px 10px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.1s;
}
.lm-list-row:hover { background: #f8fafc; }
.lm-row-active { background: #eff6ff !important; }
.lm-cell {
  font-size: 10.5px;
  color: var(--txt);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lm-trip-id { font-weight: 700; color: #2563eb; font-size: 12px; }
.lm-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.lm-badge-green  { background: #dcfce7; color: #16a34a; }
.lm-badge-yellow { background: #fef9c3; color: #a16207; }
.lm-badge-red    { background: #fee2e2; color: #dc2626; }
.lm-badge-grey   { background: #f1f5f9; color: #64748b; }
.lm-load-more {
  text-align: center;
  padding: 8px;
  font-size: 10px;
  color: #94a3b8;
}
.lm-dt-search {
  padding: 8px 10px 4px;
  flex-shrink: 0;
}
.lm-datatable {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.lm-datatable .v-data-table__wrapper {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
.lm-datatable .v-data-footer {
  border-top: 1px solid var(--bdr) !important;
  background: #f8fafc;
  padding: 0 8px;
  min-height: 36px;
  flex-shrink: 0;
  justify-content: flex-start !important;
  flex-wrap: nowrap;
}
.lm-datatable .v-data-footer__select {
  display: none;
}
.lm-datatable .v-data-footer__info {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  margin-right: auto;
}
.lm-datatable .v-data-footer__icons-before,
.lm-datatable .v-data-footer__icons-after {
  margin: 0;
}
.lm-datatable .v-data-footer__pagination {
  font-size: 11px;
  color: #64748b;
  margin: 0 4px;
}
.lm-search-field {
  font-size: 12px;
}
.lm-map-panel {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--bdr);
  min-height: 0;
}

.header-center {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* date range filter */
.date-range {
  display: flex;
  align-items: center;
  gap: 6px;
}
.dr-icon {
  font-size: 15px;
  color: #f97316;
  flex-shrink: 0;
}
.dr-sep {
  font-size: 13px;
  color: #f97316;
  font-weight: 800;
  flex-shrink: 0;
  margin: 0 2px;
}
.dr-field {
  width: 148px;
  flex-shrink: 0;
}
.apply-btn {
  flex-shrink: 0;
  font-size: 9px !important;
  height: 28px !important;
}

.dr-field ::v-deep fieldset {
  border-color: #f97316 !important;
  border-width: 2px !important;
}
.dr-field ::v-deep .v-label,
.dr-field ::v-deep .v-label--active {
  color: #f97316 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
}
.dr-field ::v-deep input {
  color: #ea580c !important;
  font-weight: 700 !important;
  font-size: 12px !important;
}
.dr-field ::v-deep .v-icon,
.dr-field ::v-deep .v-input__append-inner .v-icon {
  color: #f97316 !important;
}
.dr-field ::v-deep .v-input__slot:hover fieldset {
  border-color: #ea580c !important;
}

/* ── body ────────────────────────────────────── */
.gceo-body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-rows: 92px minmax(0, 0.55fr) minmax(0, 0.35fr);
  gap: 4px;
  padding: 4px;
  overflow: hidden;
}

/* ── ROW 1 · KPI ──────────────────────────────── */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
  overflow: hidden;
}
.kpi-card {
  background: var(--sf);
  border-radius: var(--r);
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: var(--sh);
  border-top: 3px solid transparent;
  overflow: hidden;
  min-width: 0;
}
.kpi-card.kpi-blue {
  border-top-color: #3b82f6;
}
.kpi-card.kpi-green {
  border-top-color: #22c55e;
}
.kpi-card.kpi-amber {
  border-top-color: #f59e0b;
}
.kpi-card.kpi-amber-dk {
  border-top-color: #d97706;
}
.kpi-card.kpi-orange {
  border-top-color: #f97316;
}
.kpi-card.kpi-revenue {
  border-top-color: #14b8a6;
}
.kpi-card.kpi-cost {
  border-top-color: #ef4444;
}
.kpi-ico {
  font-size: 22px;
  color: var(--mu);
  flex-shrink: 0;
}
.kpi-ico-text {
  font-size: 18px;
  font-weight: 900;
  color: #8b5cf6;
}
.kpi-txt {
  min-width: 0;
  overflow: hidden;
}
.kpi-lbl {
  font-size: 9.5px;
  font-weight: 800;
  text-transform: uppercase;
  color: var(--mu);
  letter-spacing: 0.3px;
  white-space: nowrap;
}
.kpi-sub {
  font-size: 8px;
  font-weight: 600;
  text-transform: none;
  color: #94a3b8;
  margin-left: 4px;
  letter-spacing: 0;
}
.kpi-val {
  font-size: 22px;
  font-weight: 900;
  line-height: 1.1;
}
.kpi-card.kpi-blue .kpi-val {
  color: #3b82f6;
}
.kpi-card.kpi-green .kpi-val {
  color: #22c55e;
}
.kpi-card.kpi-amber .kpi-val {
  color: #f59e0b;
}
.kpi-card.kpi-amber-dk .kpi-val {
  color: #d97706;
}
.kpi-card.kpi-orange .kpi-val {
  color: #f97316;
}
.kpi-card.kpi-total {
  border-top-color: #8b5cf6;
}
.kpi-card.kpi-total .kpi-val {
  color: #8b5cf6;
}
.kpi-card.kpi-revenue .kpi-val {
  color: #0d9488;
}
.kpi-card.kpi-cost .kpi-val {
  color: #ef4444;
}

/* ── ROW 2 ────────────────────────────────────── */
.row2 {
  display: grid;
  grid-template-columns: minmax(0, 3.4fr) minmax(0, 3fr);
  gap: 4px;
  overflow: hidden;
}
.phones-area {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: stretch;
  gap: 4px;
  overflow: hidden;
}
.phone-frame {
  background: var(--sf);
  border-radius: 12px;
  border: 1px solid var(--bdr);
  border-top-width: 3px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  height: 100%;
}
.term-cards {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-content: stretch;
  align-items: stretch;
  gap: 4px;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}
.charts-area {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 4px;
  overflow: hidden;
}
.charts-right {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
  min-height: 0;
}
.charts-right .panel:first-child {
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.tc {
  background: var(--sf);
  color: var(--txt);
  border-radius: var(--r);
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow: hidden;
  min-width: 0;
  height: 100%;
}
.tc-hdr {
  padding: 6px 8px 5px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tc-name {
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.tc-top-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  padding: 6px 6px 4px;
  flex-shrink: 0;
}
.tc-stat {
  background: #f8fafc;
  border-radius: 5px;
  padding: 5px 3px;
  text-align: center;
  border: 1px solid #e2e8f0;
}
.tc-stat-val {
  font-size: 13px;
  font-weight: 900;
  line-height: 1;
}
.tc-stat-lbl {
  font-size: 6px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--mu);
  margin-top: 3px;
  letter-spacing: 0.3px;
}
.tc-sec-hdr {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 7px;
  font-weight: 800;
  text-transform: uppercase;
  color: var(--mu);
  letter-spacing: 0.5px;
  padding: 5px 6px 3px;
  flex-shrink: 0;
}
.tc-sec-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
}

.flow-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 3px;
  flex-shrink: 0;
  padding: 0 2px;
}
.flow-item {
  background: #f1f5f9;
  border-radius: 4px;
  padding: 6px 2px;
  text-align: center;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  min-width: 0;
}
.fi-val {
  display: block;
  font-size: 12px;
  font-weight: 900;
  color: var(--txt);
}
.fi-lbl {
  display: block;
  font-size: 7px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--mu);
  margin-top: 2px;
  white-space: nowrap;
  letter-spacing: 0;
}

.dest-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0 6px 6px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}
.dest-list::-webkit-scrollbar {
  width: 3px;
}
.dest-list::-webkit-scrollbar-track {
  background: transparent;
}
.dest-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}
.dest-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 0;
  font-size: 8px;
  font-weight: 600;
  color: #94a3b8;
  text-align: center;
}
.dest-empty-icon {
  font-size: 18px;
  color: #cbd5e1;
}
.dest-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  background: #f8fafc;
  border-radius: 4px;
  padding: 4px 6px;
  border-left: 2px solid transparent;
}
.dest-row:nth-child(1) {
  border-left-color: #14b8a6;
}
.dest-row:nth-child(2) {
  border-left-color: #3b82f6;
}
.dest-row:nth-child(3) {
  border-left-color: #f59e0b;
}
.dest-row:nth-child(4) {
  border-left-color: #a855f7;
}
.dest-row:nth-child(5) {
  border-left-color: #ef4444;
}
.dest-lbl {
  font-size: 7.5px;
  font-weight: 800;
  color: var(--txt);
  text-transform: uppercase;
  letter-spacing: 0.2px;
  line-height: 1.3;
  word-break: break-word;
}
.dest-metrics {
  display: flex;
  gap: 4px;
}
.dest-metric {
  flex: 1;
  background: #ffffff;
  border-radius: 4px;
  padding: 5px 4px;
  text-align: center;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.dm-lbl {
  font-size: 8px;
  font-weight: 800;
  text-transform: uppercase;
  color: var(--txt);
  letter-spacing: 0.3px;
  margin-top: 2px;
}
.dm-val {
  font-size: 12px;
  font-weight: 900;
  color: var(--txt);
  line-height: 1.1;
}
/* bar panel */
.bar-panel {
  background: var(--sf);
  border-radius: var(--r);
  padding: 7px;
  box-shadow: var(--sh);
  display: flex;
  flex-direction: column;
  gap: 5px;
  overflow: hidden;
  min-width: 0;
}
.bar-hdr {
  font-size: 10px;
  font-weight: 700;
  color: var(--txt);
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.bar-hdr .mdi {
  color: var(--mu);
  font-size: 11px;
}
.leg {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 8px;
  font-weight: 600;
  color: var(--mu);
}
.leg-dot {
  width: 7px;
  height: 7px;
  border-radius: 2px;
  flex-shrink: 0;
}
.leg-teu {
  font-size: 9px;
  font-weight: 800;
  margin-left: 2px;
}
.chart-wrap {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
}

/* ── ROW 3 ────────────────────────────────────── */
.row3 {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 2.25fr);
  gap: 4px;
  overflow: hidden;
}
.row3-right {
  display: grid;
  grid-template-columns: 1.3fr 0.7fr;
  gap: 4px;
  overflow: hidden;
  min-height: 0;
}
.row3-right .panel {
  overflow: hidden;
  min-height: 0;
}
.loc-panel {
  background: var(--sf);
  border-radius: var(--r);
  padding: 6px 10px;
  box-shadow: var(--sh);
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
}
.loc-panel .loc-tbl {
  flex: 1;
}
.p-hdr {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--txt);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  margin-bottom: 4px;
}
.p-hdr .mdi {
  color: var(--mu);
  font-size: 13px;
}
.p-sub {
  font-size: 9px;
  font-weight: 600;
  color: var(--mu);
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
}

.loc-tbl {
  width: 100%;
  height: 100%;
  border-collapse: collapse;
}
.loc-tbl tbody {
  height: 100%;
}
.loc-tbl tbody tr {
  height: 1%;
}
.loc-tbl th {
  padding: 3px 6px;
  font-size: 9px;
  font-weight: 900;
  text-transform: uppercase;
  color: var(--mu);
  letter-spacing: 0.2px;
  border-bottom: 1px solid var(--bdr);
  text-align: left;
  background: transparent;
  white-space: nowrap;
}
.loc-tbl td {
  padding: 3px 6px;
  border-bottom: 1px solid var(--bdr);
  font-weight: 600;
  white-space: nowrap;
  font-size: 10px;
  color: var(--txt);
  vertical-align: middle;
}
.loc-tbl tbody tr:hover {
  background: #f8fafc;
}
.loc-name {
  font-weight: 800;
  color: var(--txt);
}
.dom {
  color: #64748b;
  font-size: 10px;
}
.badge {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 9.5px;
  font-weight: 800;
  min-width: 26px;
  text-align: center;
}
.badge.sub {
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
}
.badge.int {
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

/* exit / port exit panels */
.exit-panel {
  background: var(--sf);
  border-radius: var(--r);
  padding: 10px 12px;
  box-shadow: var(--sh);
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
}
.panel {
  background: var(--sf);
  border-radius: var(--r);
  padding: 8px 10px;
  box-shadow: var(--sh);
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
  min-width: 0;
}

.cr-chart-wrap {
  flex: 1;
  min-height: 0;
  position: relative;
}
.cr-chart-wrap canvas {
  width: 100% !important;
  height: 100% !important;
}
.cr-dialog-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.cr-dialog-table thead tr {
  background: #f8fafc;
  position: sticky;
  top: 0;
}
.cr-dialog-table th {
  padding: 8px 12px;
  text-align: left;
  color: #475569;
  font-weight: 700;
  font-size: 11px;
  border-bottom: 1px solid #e2e8f0;
}
.cr-dialog-table td {
  padding: 7px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}
.cr-dialog-table tbody tr:hover td {
  background: #f8fafc;
}
.cr-dialog-table .tr-name {
  font-weight: 600;
  color: #1e293b;
}
.cr-dialog-table .td-rev {
  color: #0d9488;
  font-weight: 600;
}
.cr-dialog-table .td-cost {
  color: #d97706;
  font-weight: 600;
}

.btrack {
  flex: 1;
  height: 5px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
  min-width: 0;
}
.bfill {
  height: 100%;
  border-radius: 3px;
}

/* pie */
.pie-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.pie-box {
  flex-shrink: 0;
  width: 130px;
  height: 130px;
  position: relative;
  overflow: hidden;
}
.pie-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 16px 0;
}
.pie-leg {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
}
.dl-row {
  display: flex;
  align-items: center;
  gap: 5px;
}
.dl-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}
.dl-lbl {
  font-size: 9px;
  font-weight: 600;
  color: var(--txt);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}
.dl-pct {
  font-size: 9px;
  font-weight: 700;
  color: var(--mu);
  flex-shrink: 0;
}

/* port exit */
.pe-hdr-row {
  justify-content: space-between;
  width: 100%;
}
.pe-total {
  font-size: 11px;
  font-weight: 900;
  color: #3b82f6;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 5px;
  padding: 1px 6px;
  flex-shrink: 0;
  white-space: nowrap;
}
.pe-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scroll-behavior: smooth;
}
.pe-list::-webkit-scrollbar {
  width: 3px;
}
.pe-list::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 3px;
}
.pe-loading {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
  padding: 4px 0;
  font-size: 8px;
  color: var(--mu);
}
.pe-loading .mdi {
  font-size: 10px;
  color: #f97316;
}
.pe-end {
  text-align: center;
  font-size: 7.5px;
  color: var(--mu);
  padding: 4px 0;
}
.pe-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 20px 0;
}
.pe-empty-icon {
  font-size: 22px;
  color: #334155;
}
.pe-empty-txt {
  font-size: 8px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.pe-row {
  display: grid;
  grid-template-columns: 148px 1fr 44px;
  align-items: center;
  gap: 5px;
}
.pe-lbl {
  font-size: 10px;
  font-weight: 600;
  color: var(--txt);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  min-width: 0;
}
.pe-val {
  font-size: 10px;
  font-weight: 800;
  text-align: left;
  padding-left: 6px;
  white-space: nowrap;
}
</style>
